import { useCallback, useRef, useState, type CSSProperties, type KeyboardEvent } from 'react';

import { formatDate } from '@/utils/date';

import { onActivateKey } from './activate';
import { Calendar } from './Calendar';
import { FieldLabel } from './FieldLabel';
import { Portal } from './Portal';
import { useOutsideDismiss, usePopupPosition } from './usePopupPosition';

/** Pendant zu `SfDatePicker`; Eingabeformat `dd.MM.yyyy`. */
export interface DatePickerProps {
  value: Date | null;
  onChange: (value: Date | null) => void;
  /** Feste Beschriftung über dem Feld (REQ-24 §2). */
  label?: string;
  /** Zusatz in schwächerer Schrift hinter der Beschriftung. */
  hint?: string;
  /** Nur für echte Beispielwerte — nicht als Ersatz für `label` (REQ-24 §3.2). */
  placeholder?: string;
  readOnly?: boolean;
  disabled?: boolean;
  id?: string;
  className?: string;
  style?: CSSProperties;
  title?: string;
}

const GERMAN_DATE = /^(\d{1,2})\.(\d{1,2})\.(\d{4})$/;

/** Liest `dd.MM.yyyy`; alles andere ergibt `null`. */
function parseGermanDate(text: string): Date | null {
  const match = GERMAN_DATE.exec(text.trim());
  if (match === null) return null;

  const [, day, month, year] = match;
  const date = new Date(Number(year), Number(month) - 1, Number(day));
  return date.getMonth() === Number(month) - 1 && date.getDate() === Number(day) ? date : null;
}

export function DatePicker(props: DatePickerProps) {
  const { onChange } = props;
  const [open, setOpen] = useState(false);
  const [focused, setFocused] = useState(false);
  const [draft, setDraft] = useState<string | null>(null);
  const anchorRef = useRef<HTMLElement>(null);
  const popupRef = useRef<HTMLDivElement>(null);

  const close = useCallback(() => {
    setOpen(false);
  }, []);
  useOutsideDismiss(open, close, [anchorRef, popupRef]);
  const popupStyle = usePopupPosition({ open, anchorRef, popupRef });

  const text = draft ?? formatDate(props.value);

  const commit = () => {
    if (draft === null) return;
    setDraft(null);
    const parsed = draft.trim() === '' ? null : parseGermanDate(draft);
    if (parsed?.getTime() !== props.value?.getTime()) onChange(parsed);
  };

  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') commit();
    if (event.key === 'Escape') {
      setDraft(null);
      setOpen(false);
    }
  };

  const toggle = () => {
    if (props.disabled !== true && props.readOnly !== true) setOpen((value) => !value);
  };

  return (
    <>
      <FieldLabel
        id={props.id}
        elementRef={anchorRef}
        className={props.className}
        style={props.style}
        title={props.title}
        groupClass={open ? 'e-date-wrapper e-dateinput-active' : 'e-date-wrapper'}
        label={props.label}
        hint={props.hint}
        focused={focused}
        disabled={props.disabled}
      >
        {({ id }) => (
          <>
            <input
              id={id}
              type="text"
              className="e-input"
              value={text}
              placeholder={props.placeholder}
              readOnly={props.readOnly === true}
              disabled={props.disabled === true}
              onChange={(event) => {
                setDraft(event.target.value);
              }}
              onFocus={() => {
                setFocused(true);
              }}
              onBlur={() => {
                setFocused(false);
                commit();
              }}
              onKeyDown={onKeyDown}
            />
            <span
              role="button"
              tabIndex={0}
              aria-label="Kalender öffnen"
              aria-expanded={open}
              className="e-input-group-icon e-date-icon e-icons fa-solid fa-calendar-days"
              onClick={toggle}
              onKeyDown={onActivateKey(toggle)}
            />
          </>
        )}
      </FieldLabel>
      {open ? (
        <Portal>
          {/* Lage und Ebene liefert `.e-popup` aus dem Stylesheet (REQ-15). */}
          <div ref={popupRef} className="e-popup" style={popupStyle}>
            <Calendar
              selected={props.value}
              onSelect={(date) => {
                setDraft(null);
                setOpen(false);
                onChange(date);
              }}
            />
          </div>
        </Portal>
      ) : null}
    </>
  );
}

export default DatePicker;
